<template>
  <WidgetPanel>
    <template #header>
      <h3 class="widget-headline">
        Überblick
      </h3>
    </template>
    <template #default>
      <section v-if="experiment">
        <div class="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl class="sm:divide-y sm:divide-gray-200">
            <div v-if="experiment.practice" class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                Methode
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                <div class="flex items-center">
                  <Twemoji :code="experiment.practice.emoji" class="w-6 h-6 mr-2" />
                  <router-link :to="{name: 'practices.show', params: {id: experiment.practice.id}}">
                    {{ experiment.practice.title }}
                  </router-link>
                </div>
              </dd>
            </div>
            <div v-if="experiment.practice" class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                Beschreibung
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 flex flex-col">
                <span>
                {{ experiment.practice.teaser }}
                </span>
                <router-link :to="{name: 'practices.show.content', params: {id: experiment.practice.id}}">
                  &nbsp;<br/>
                  ➽ <u>Vollständige Beschreibung und Anwendungstipps auf der Detailseite zur Methode</u>
                </router-link>
              </dd>
            </div>
            <div v-if="experiment.user" class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                Ersteller*in
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                <div class="flex items-center">
                  <UserCircleIcon class="w-6 h-6 mr-2" />
                  <router-link :to="{name: 'profile', params: {id: experiment.user.id}}">
                    {{ experiment.user.name }}
                  </router-link>
                </div>
              </dd>
            </div>
            <div v-if="!empty" class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                Fortschritt
              </dt>
              <dd class="mt-1 text-sm sm:col-span-2 sm:mt-0">
                <span :class="{'text-gray-900': !clear, 'text-green-700': clear}">
                  {{ dones }} von {{ number }} Aufgaben erledigt
                </span>
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                Startdatum
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {{ $d(experiment.starts_at, 'short') }}
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                Enddatum
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {{ $d(experiment.ends_at, 'short') }}
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                Dauer
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {{ experiment.days_total }} Tage
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                1. Implementierung
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                Starte mit der Umsetzung der Methode innerhalb und außerhalb deines Podcasts. Nutze dafür die inhaltliche Beschreibung als Wegweiser, sowie ggf. die Aktionsschritte hier am Experiment (diese werden fortlaufend ergänzt).
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                2. Datenerhebung
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                Nimm dir nun etwas Zeit die Methode wirken zu lassen. Wenn du möchtest, kannst du jederzeit Zwischenwerte eintragen, falls dir bei einzelnen Metriken schon etwas aufgefallen ist.
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt class="text-sm font-medium text-gray-500">
                3. Finalauswertung
              </dt>
              <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                Nach dem Ablauf des Experiments bitte ich dich eine Auswertung vorzunehmen und die Effekte auf deine Wachstumsziele zu beurteilen. Damit überprüfe ich die Wirksamkeit und passe ggf. meine Empfehlungen für nächste Schritte an.
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </template>
  </WidgetPanel>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({experiment: Object});
const models = computed(() => props.experiment.actions ?? []);
const number = computed(() => models.value.length);
const empty = computed(() => number.value === 0);
const dones = computed(() => models.value.filter(r => r.completed).length);
const clear = computed(() => number.value === dones.value);
</script>
